import { Button as FluentButton } from '@fluentui/react-components';
import React, { forwardRef } from 'react';

import { Spinner } from '../Spinner';

import { useClassNames } from './Button.styles';
import type { ButtonProps } from './Button.types';

const getDisplayIcon = (loading: boolean, icon: ButtonProps['icon'], hasChildren: boolean, ariaLabel?: string) => {
  if (!loading) {
    return icon;
  }

  if (!hasChildren) {
    return <Spinner size="tiny" aria-label={ariaLabel} />;
  }

  return null;
};

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement | null, ButtonProps>((props, ref) => {
  const {
    fullWidth = false,
    disabled = false,
    loading = false,
    loadingLabel,
    icon,
    className,
    appearance,
    ...baseButtonProps
  } = props;

  const isPrimaryBlue = appearance === 'primaryBlue';
  const isDisabled = disabled || loading;
  const classNames = useClassNames(fullWidth, className, isPrimaryBlue, isDisabled);
  const hasChildren = (baseButtonProps.children as React.ReactNode) != null;
  const ariaLabel = loading && loadingLabel ? loadingLabel : baseButtonProps['aria-label'];

  const content: React.ReactNode =
    loading && hasChildren ? (
      <span className={classNames.loadingContent}>
        <span className={classNames.hiddenChildren}>{baseButtonProps.children as React.ReactNode}</span>
        <span className={classNames.centeredSpinner}>
          <Spinner size="tiny" aria-label={ariaLabel} />
        </span>
      </span>
    ) : (
      (baseButtonProps.children as React.ReactNode)
    );
  const passedAppearance = isPrimaryBlue ? 'primary' : appearance;
  const hrefProps: Record<string, string> = baseButtonProps.href ? { as: 'a', target: '_blank' } : {};

  return (
    <FluentButton
      {...baseButtonProps}
      {...hrefProps}
      ref={ref}
      appearance={passedAppearance}
      disabled={isDisabled}
      icon={getDisplayIcon(loading, icon, hasChildren, ariaLabel)}
      aria-label={ariaLabel}
      className={classNames.root}
    >
      {content}
    </FluentButton>
  );
});

Button.displayName = 'Button';
